/**
 * 곡사 화살(`BOW_LOB`)의 포물선. 시뮬레이션의 화살은 땅 위를 곧게 간다 — 위로 뜨는 것은 그리기만의 일이다.
 *
 * 화면이 곧게 그리면 곡사가 곧은 화살과 구별되지 않고, 앞줄의 적을 그냥 지나쳐 가는 것처럼 보인다(날아가는 동안에는 아무도
 * 맞지 않는다). 포물선으로 띄워 "넘어간다"를 보인다.
 *
 * 띄우기만으로는 모자랐다. 우리 궁수는 대개 화면 위쪽의 적을 쏜다 — 위로 띄우면 날아가는 방향과 같은 축이라 화살이 휘지
 * 않고 빨라졌다 느려질 뿐이었다(QA가 쟀다: 곧은 길에서 벗어난 거리가 중앙 10px, 곧은 화살은 4px). 그래서 높이를 두 가지로
 * 더 보인다: 땅에 그림자를 남기고(화살과 그림자가 벌어진 만큼이 높이다), 화살의 크기와 길이를 바꾼다(`lobScale`).
 */

/** 꼭짓점 높이: 날아가는 틱 수에 비례하고(먼 화살일수록 높다) 위아래를 묶는다(px). */
const LIFT_PER_TICK = 2.2;
const LIFT_MIN = 14;
const LIFT_MAX = 64;

export interface LobArc {
  /** 땅에서 뜬 높이(px, 화면에서는 위로). */
  lift: number;
  /** 꼭짓점에 견준 높이(0~1). 화살을 키우고 그림자를 흐리게 하는 데 쓴다. */
  high: number;
  /** 높이가 변하는 빠르기(px/틱, 오르는 동안 양수). 화살 머리의 기울기에 쓴다. */
  rise: number;
}

/**
 * 지금의 높이와 오르내리는 빠르기. `life`는 쏠 때의 수명, `ttl`은 남은 수명(이번 틱을 마친 값), `alpha`는 틱 사이 보간(0~1).
 * 화면은 지난 자리와 지금 자리 사이를 그리므로, 지금 자리(alpha 1)가 `life − ttl`틱을 난 것이다.
 */
export function lobArc(life: number, ttl: number, alpha: number): LobArc {
  if (life <= 0) return { lift: 0, high: 0, rise: 0 };
  const t = Math.min(1, Math.max(0, (life - ttl - 1 + alpha) / life));
  const top = Math.min(LIFT_MAX, Math.max(LIFT_MIN, life * LIFT_PER_TICK));
  const high = 4 * t * (1 - t);
  return { lift: top * high, high, rise: (4 * top * (1 - 2 * t)) / life };
}

/** 꼭짓점에 뜬 화살을 이만큼 더 크게 그린다(가까이 온 것처럼). */
const LOB_GROW = 0.15;
/** 쏜 직후와 떨어지기 직전의 화살 길이(꼭짓점에 견준 비). 위에서 내려다보면 기울어진 화살은 짧게 보인다. */
const LOB_SHORT = 0.5;

/**
 * 곡사 화살의 크기: `len`은 화살의 길이 쪽, `wide`는 굵기 쪽 배율.
 *
 * - 쏜 직후에는 위로 기울어 있어 **짧게**, 꼭짓점에서는 누워 **온 길이**(가까워진 만큼 1.15배), 떨어질 때는 반대로 기울어
 *   다시 **짧게** 보인다. 플레이한 사람이 정한 모양이다.
 * - 처음에는 꼭짓점에서 1.45배로 통째로 키웠다. 폰에서 "적이 엄청 큰 화살을 날린다"로 보였다(플레이한 사람이 짚었다) —
 *   높이를 읽히려던 것이 다른 무기처럼 보였다. 높이는 그림자가 말하고, 크기는 조금만 보탠다.
 *
 * 기울기는 포물선의 오르내리는 빠르기에 비례한다: 꼭짓점에서 0, 양 끝에서 가장 크다. `high = 1 − (기울기 비)²`이므로
 * 기울기 비는 `√(1 − high)`다.
 */
export function lobScale(high: number): { len: number; wide: number } {
  const h = Math.min(1, Math.max(0, high));
  const wide = 1 + LOB_GROW * h;
  return { len: wide * (1 - (1 - LOB_SHORT) * Math.sqrt(1 - h)), wide };
}
